import React, { useState } from 'react';
import { Plus, Trash2, Bell, Users, School } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDateTime, generateId } from '../../utils/helpers';

const emptyForm = () => ({
  title: '', content: '', priority: 'medium', isActive: true,
  targetRoles: ['student', 'father', 'mother', 'teacher'],
  targetClassIds: [],      // empty = all classes
  includeClassTeachers: true,
});

const PRIORITY_COLORS = {
  high:   'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low:    'bg-green-100 text-green-700 border-green-200',
};

function Announcements() {
  const { announcements, classes, saveAnnouncement, updateAnnouncement, deleteAnnouncement } = useApp();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const isTeacher = currentUser?.role === 'teacher';
  const canCreate = isAdmin || isTeacher;

  const [modal, setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]     = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [allClasses, setAllClasses] = useState(true); // toggle for class targeting

  const role = currentUser?.role;

  // Visible to current user
  const visible = announcements.filter(a => {
    if (!a.isActive) return isAdmin && a.source !== 'system';
    if (role === 'admin') return a.source !== 'system';
    const roles = a.targetRoles || [];
    // Treat parent/father/mother as equivalent so old announcements still reach new roles
    const parentRoles = ['parent','father','mother'];
    const isParentUser = parentRoles.includes(role);
    const hasParentRole = roles.some(r => parentRoles.includes(r));
    if (roles.length && !roles.includes('all') && !roles.includes(role) && !(isParentUser && hasParentRole)) return false;
    // Class filter: if targetClassIds is set, only show if user is in that class
    const classIds = a.targetClassIds || [];
    if (classIds.length > 0) {
      // Teachers: check if class teacher of targeted class
      if (role === 'teacher') return a.includeClassTeachers;
      // Students: check classId
      // (for simplicity on web: show to all if user can't determine class — backend controls push)
    }
    return true;
  });

  // Grouped classes by name for the selector
  const classGroups = classes.reduce((acc, c) => {
    if (!acc[c.name]) acc[c.name] = [];
    acc[c.name].push(c);
    return acc;
  }, {});
  const sortedClassNames = Object.keys(classGroups).sort((a, b) => {
    const nA = parseInt(a.replace(/\D/g, '')) || 0;
    const nB = parseInt(b.replace(/\D/g, '')) || 0;
    return nA !== nB ? nA - nB : a.localeCompare(b);
  });

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setAllClasses(true);
    setModal(true);
  };
  const openEdit = (ann) => {
    setEditing(ann);
    const hasClasses = ann.targetClassIds?.length > 0;
    setAllClasses(!hasClasses);
    setForm({
      title: ann.title || '',
      content: ann.content || '',
      priority: ann.priority || 'medium',
      isActive: ann.isActive !== false,
      targetRoles: ann.targetRoles || ['student', 'father', 'mother', 'teacher'],
      targetClassIds: ann.targetClassIds || [],
      includeClassTeachers: ann.includeClassTeachers !== false,
    });
    setModal(true);
  };

  const toggleRole = (r) => setForm(f => ({
    ...f,
    targetRoles: f.targetRoles.includes(r) ? f.targetRoles.filter(x => x !== r) : [...f.targetRoles, r],
  }));

  const toggleClass = (classId) => setForm(f => ({
    ...f,
    targetClassIds: f.targetClassIds.includes(classId)
      ? f.targetClassIds.filter(id => id !== classId)
      : [...f.targetClassIds, classId],
  }));

  const toggleAllInGroup = (name) => {
    const groupIds = classGroups[name].map(c => c.id);
    const allSelected = groupIds.every(id => form.targetClassIds.includes(id));
    setForm(f => ({
      ...f,
      targetClassIds: allSelected
        ? f.targetClassIds.filter(id => !groupIds.includes(id))
        : [...new Set([...f.targetClassIds, ...groupIds])],
    }));
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        targetClassIds: allClasses ? [] : form.targetClassIds,
        id: editing?.id || generateId('ANN'),
        createdBy: currentUser?.profileId || '',
        createdAt: editing?.createdAt || new Date().toISOString(),
      };
      if (editing) {
        await updateAnnouncement(editing.id, payload);
      } else {
        await saveAnnouncement(payload);
      }
      setModal(false);
    } catch (e) { alert('Error: ' + e.message); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    await deleteAnnouncement(id);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Announcements</h1>
          <p className="text-gray-500 text-sm mt-0.5">{visible.length} announcement{visible.length !== 1 ? 's' : ''}</p>
        </div>
        {canCreate && (
          <button onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium">
            <Plus size={16} /> New Announcement
          </button>
        )}
      </div>

      {/* List */}
      <div className="space-y-4">
        {visible.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-gray-400">
            <Bell size={36} className="mx-auto mb-3 opacity-30" />
            <p>No announcements yet</p>
          </div>
        )}
        {visible.map(ann => {
          const classIds = ann.targetClassIds || [];
          const classNames = classIds.map(id => {
            const c = classes.find(cl => cl.id === id);
            return c ? `${c.name}-${c.section}` : id;
          });

          return (
            <div key={ann.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <Bell size={18} className="text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 text-base">{ann.title}</h3>
                      {ann.priority && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium border capitalize ${PRIORITY_COLORS[ann.priority] || PRIORITY_COLORS.medium}`}>
                          {ann.priority}
                        </span>
                      )}
                      {ann.source === 'system' && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-600 border border-blue-200">
                          Auto
                        </span>
                      )}
                      {!ann.isActive && <Badge value="inactive" />}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 whitespace-pre-wrap">{ann.content}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                      <span>{formatDateTime(ann.createdAt)}</span>
                      {/* Audience */}
                      <span className="flex items-center gap-1">
                        <Users size={11} />
                        {(ann.targetRoles || []).length > 0
                          ? ann.targetRoles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')
                          : 'All Roles'}
                      </span>
                      {/* Classes */}
                      <span className="flex items-center gap-1">
                        <School size={11} />
                        {classIds.length === 0
                          ? 'All Classes'
                          : classNames.length <= 3
                            ? classNames.join(', ')
                            : `${classNames.slice(0, 3).join(', ')} +${classNames.length - 3} more`}
                      </span>
                      {ann.includeClassTeachers && (
                        <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                          + Class Teachers
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {canCreate && (
                  <div className="flex gap-1 flex-shrink-0">
                    {ann.source !== 'system' && (
                      <button onClick={() => openEdit(ann)}
                        className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                    )}
                    <button onClick={() => remove(ann.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)}
        title={editing ? 'Edit Announcement' : 'New Announcement'} size="lg">
        <div className="space-y-4 p-1">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">Title *</label>
            <input value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="Announcement title" />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium mb-1">Message *</label>
            <textarea rows={4} value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none"
              placeholder="Write your announcement…" />
          </div>

          {/* Priority + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Priority</label>
              <select value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select value={form.isActive ? 'active' : 'inactive'}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.value === 'active' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Target Roles */}
          <div>
            <label className="block text-sm font-medium mb-2">Send To (Roles)</label>
            <div className="flex flex-wrap gap-2">
              {['student', 'father', 'mother', 'teacher', 'admin'].map(r => (
                <label key={r}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-colors ${form.targetRoles.includes(r) ? 'bg-purple-100 border-purple-400 text-purple-700 font-medium' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                  <input type="checkbox" className="hidden"
                    checked={form.targetRoles.includes(r)}
                    onChange={() => toggleRole(r)} />
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </label>
              ))}
            </div>
          </div>

          {/* Target Classes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Target Classes</label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                <div
                  onClick={() => { setAllClasses(v => !v); if (!allClasses) setForm(f => ({ ...f, targetClassIds: [] })); }}
                  className={`w-9 h-5 rounded-full relative transition-colors ${allClasses ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${allClasses ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
                All Classes
              </label>
            </div>

            {!allClasses && (
              <div className="border border-gray-200 rounded-xl p-3 max-h-52 overflow-y-auto space-y-3">
                {sortedClassNames.map(name => {
                  const groupIds = classGroups[name].map(c => c.id);
                  const allSelected = groupIds.every(id => form.targetClassIds.includes(id));
                  const someSelected = groupIds.some(id => form.targetClassIds.includes(id));
                  return (
                    <div key={name}>
                      <label className="flex items-center gap-2 cursor-pointer mb-1">
                        <input type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleAllInGroup(name)}
                          className="w-4 h-4 accent-purple-600" />
                        <span className="text-sm font-semibold text-gray-700">{name}</span>
                        <span className="text-xs text-gray-400">({groupIds.length} section{groupIds.length !== 1 ? 's' : ''})</span>
                      </label>
                      <div className="flex flex-wrap gap-1.5 ml-6">
                        {classGroups[name].sort((a, b) => a.section.localeCompare(b.section)).map(c => (
                          <label key={c.id}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border cursor-pointer text-xs transition-colors ${form.targetClassIds.includes(c.id) ? 'bg-purple-100 border-purple-400 text-purple-700 font-medium' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                            <input type="checkbox" className="hidden"
                              checked={form.targetClassIds.includes(c.id)}
                              onChange={() => toggleClass(c.id)} />
                            Sec {c.section}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Include class teachers */}
            <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm text-gray-600">
              <input type="checkbox"
                checked={form.includeClassTeachers}
                onChange={e => setForm(f => ({ ...f, includeClassTeachers: e.target.checked }))}
                className="w-4 h-4 accent-purple-600" />
              <span>Also notify <strong>Class Teachers</strong> of selected classes</span>
            </label>
          </div>

          {/* Selected summary */}
          {!allClasses && form.targetClassIds.length > 0 && (
            <div className="text-xs text-purple-600 bg-purple-50 rounded-lg px-3 py-2">
              {form.targetClassIds.length} class section{form.targetClassIds.length !== 1 ? 's' : ''} selected
              {form.includeClassTeachers ? ' + class teachers will be notified' : ''}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={() => setModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !form.title.trim() || !form.content.trim()}
            className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60">
            {saving ? 'Posting…' : editing ? 'Update' : '📢 Post Announcement'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Announcements;
